/**
 * Plan context — reads the `plan` field from LangGraph thread state
 * and exposes it to the ProgressTracker in the ViewportFooter.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Types (mirrors the backend Plan / PlanStep schema)
// ---------------------------------------------------------------------------

export interface PlanStep {
  title: string;
  status: 'pending' | 'in_progress' | 'completed';
  description: string;
}

export interface Plan {
  title: string;
  steps: PlanStep[];
  started_at: string;
  completed_at: string | null;
}

// ---------------------------------------------------------------------------
// Extract from state values
// ---------------------------------------------------------------------------

/**
 * Extract the plan from LangGraph thread state values.
 * Returns null if no plan exists or the shape doesn't match.
 */
export function extractPlan(values: Record<string, unknown> | undefined): Plan | null {
  if (!values) return null;
  const raw = values.plan;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const plan = raw as Record<string, unknown>;
  if (typeof plan.title !== 'string' || !Array.isArray(plan.steps)) return null;
  return raw as unknown as Plan;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface PlanContextValue {
  plan: Plan | null;
  /** Thread ID this plan belongs to. Used to detect staleness. */
  threadId: string | null;
  setPlan: (plan: Plan | null, threadId?: string | null) => void;
}

const PlanContext = createContext<PlanContextValue | null>(null);

export function PlanProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ plan: Plan | null; threadId: string | null }>({
    plan: null,
    threadId: null,
  });

  const setPlan = useCallback((newPlan: Plan | null, threadId?: string | null) => {
    setState({ plan: newPlan, threadId: threadId ?? null });
  }, []);

  return (
    <PlanContext.Provider value={{ plan: state.plan, threadId: state.threadId, setPlan }}>
      {children}
    </PlanContext.Provider>
  );
}

const FALLBACK: PlanContextValue = { plan: null, threadId: null, setPlan: () => {} };

export function usePlan(): PlanContextValue {
  const ctx = useContext(PlanContext);
  return ctx ?? FALLBACK;
}
